import { Observable } from 'rxjs';
import { ApplicationsService } from '../../../orval-gen/endpoints/applications/applications.service';
import { AgencyApplicationsList200DataItem } from '../../../orval-gen/models';

export type Application = AgencyApplicationsList200DataItem;

/** Up to two initials, used as the logo fallback when an app has no image. */
export function appInitials(label: string): string {
  return (label ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w.charAt(0).toUpperCase())
    .join('');
}

/** The connection's state as a badge. An app without a connection has not been requested. */
export function connectionBadge(app: Application): { label: string; cls: string } {
  switch (app.connection?.status) {
    case 'active':
      return { label: 'Enabled', cls: 'badge-default' };
    case 'pending':
      return { label: 'Pending approval', cls: 'badge-secondary' };
    case 'suspended':
      return { label: 'Suspended', cls: 'badge-outline' };
    case 'rejected':
      return { label: 'Rejected', cls: 'badge-destructive' };
    default:
      return { label: 'Not requested', cls: 'badge-outline' };
  }
}

/**
 * Whether the agency may drop the connection. A suspended one is the developer's to resume: the hub already
 * refuses its key, and suspension keeps the key, so dropping it would only lose the key and the approval.
 */
export function canDrop(app: Application): boolean {
  return app.connection?.status !== 'suspended';
}

/** Label of the button that requests the app, or drops its connection whatever its state. */
export function toggleLabel(app: Application): string {
  switch (app.connection?.status) {
    case undefined:
      return 'Request access';
    case 'active':
      return 'Revoke access';
    case 'pending':
      return 'Withdraw request';
    default:
      return 'Remove';
  }
}

/** Copy for the request / drop confirmation dialog. */
export function toggleConfirmation(app: Application): { title: string; message: string } {
  switch (app.connection?.status) {
    case undefined:
      return {
        title: `Request access to ${app.name}?`,
        message:
          'This asks the app’s developer for access to your airline content for shopping, booking and issuing orders. The app can use it once they approve. You can revoke it at any time.',
      };
    case 'active':
      return {
        title: `Revoke access for ${app.name}?`,
        message:
          'The application won’t be able to access your airline content anymore. You can enable it again later.',
      };
    case 'pending':
      return {
        title: `Withdraw the request for ${app.name}?`,
        message: 'The developer will no longer see your request. You can request it again later.',
      };
    default:
      return {
        title: `Remove ${app.name}?`,
        message: 'This removes the connection, so you can request the application again.',
      };
  }
}

/** Enables an app that has no connection, or drops the connection it has. */
export function toggleConnection(
  service: ApplicationsService,
  app: Application
): Observable<unknown> {
  return app.connection
    ? service.agencyApplicationsDisable(app.id)
    : service.agencyApplicationsEnable(app.id);
}

/** Toast shown once {@link toggleConnection} succeeds, worded by the state the app was in. */
export function toggleSuccessMessage(app: Application): string {
  switch (app.connection?.status) {
    case undefined:
      return `Access requested. ${app.name} can use your content once its developer approves.`;
    case 'active':
      return `Access revoked for ${app.name}.`;
    case 'pending':
      return `Request for ${app.name} withdrawn.`;
    default:
      return `${app.name} removed.`;
  }
}
